import { useEffect, useState } from 'react';
import {
  DATA_MODES224,
  ONBOARDING_STEPS224,
  activationRefusalLabel224,
  dataModeLabels224,
  onboardingStepLabels224,
  reportCaption224,
  restoreRefusalLabel224,
  seatLabel224,
  serviceCountLabel224,
  serviceLabels224,
  tenantKindLabels224,
  tenantStatusLabels224,
  type DataMode224,
  type OnboardingStep224,
  type ServiceRow224,
  type TenantKind224,
  type TenantStatus224,
} from '../domain/partnerConsole224';
import { AppFrame, Card, Metric, PrimaryButton, SecondaryButton, StatusBanner } from '../components/ui';
import { GuideCharacter } from '../reactions/GuideCharacter';
import { fetchConsoleOverview224, fetchConsoleTenants224, saveRestrictedFeatures224, type ConsoleOverview224, type ConsoleTenant224 } from '../services/partnerConsoleApi';
import { DEMO_FEATURES224, demoFeatureLabels224, demoFeatureNotes224, type DemoFeature224 } from '../domain/demoRestrictions224';
import { useAppStore } from '../store/AppStore';

interface DemoTenant {
  id: string; name: string; kind: TenantKind224; dataMode: DataMode224; status: TenantStatus224;
  seatLimit: number; children: number; activeChildren: number; classrooms: number;
}

const demoTenants: DemoTenant[] = [
  { id: 'tenant_snow_edu', name: '스노우 교육지원청', kind: 'school', dataMode: 'real', status: 'active', seatLimit: 120, children: 96, activeChildren: 71, classrooms: 4 },
  { id: 'tenant_direct', name: '개인 가입 가족', kind: 'direct', dataMode: 'real', status: 'active', seatLimit: 0, children: 412, activeChildren: 268, classrooms: 0 },
  { id: 'tenant_star_trial', name: '별빛 학습센터 (체험)', kind: 'academy', dataMode: 'institution-demo', status: 'onboarding', seatLimit: 40, children: 28, activeChildren: 19, classrooms: 2 },
  { id: 'tenant_expo_booth', name: '교육박람회 시연 부스', kind: 'public', dataMode: 'sales-demo', status: 'active', seatLimit: 30, children: 30, activeChildren: 30, classrooms: 1 },
];

const demoServices: ServiceRow224[] = [
  { serviceId: 'mrm', status: 'connected', events: 18420, linkedUsers: 536 },
  { serviceId: 'lidia', status: 'pending', events: null, linkedUsers: null },
  { serviceId: 'bookquest', status: 'pending', events: null, linkedUsers: null },
  { serviceId: 'nexmap', status: 'pending', events: null, linkedUsers: null },
  { serviceId: 'core', status: 'connected', events: 18420, linkedUsers: 536 },
];

const demoRefusals = ['CHECKSUM_MISMATCH', 'SCHEMA_TOO_NEW', 'FOREIGN_INSTANCE', 'SNAPSHOT_EMPTY'];

export function PartnerConsoleDemoScreen224() {
  const [mode, setMode] = useState<DataMode224>('real');
  const [steps, setSteps] = useState<OnboardingStep224[]>(['contract', 'admin', 'organization']);
  const [refusal, setRefusal] = useState('');

  const inMode = demoTenants.filter((tenant) => tenant.dataMode === mode);
  const totals = inMode.reduce((sum, tenant) => ({
    children: sum.children + tenant.children,
    activeChildren: sum.activeChildren + tenant.activeChildren,
    classrooms: sum.classrooms + tenant.classrooms,
    seats: sum.seats + tenant.seatLimit,
  }), { children: 0, activeChildren: 0, classrooms: 0, seats: 0 });

  const missing = ONBOARDING_STEPS224.filter((step) => !steps.includes(step));
  const activationRefusal = missing.length ? `ONBOARDING_INCOMPLETE:${missing.join(',')}` : '';
  const toggleStep = (step: OnboardingStep224) => setSteps((current) =>
    current.includes(step) ? current.filter((item) => item !== step) : [...current, step]);

  return <AppFrame title="MRM 2.24 파트너 콘솔 데모" backTo="/demo" screenClass="console-screen-2-24">
    <div className="demo-sample-banner">체험용 고정 운영 데이터 · 실제 계정과 완전 분리</div>
    <StatusBanner />

    <section className="console-hero-2-24">
      <GuideCharacter guide="snows" pose="checklist" state="guide" alt="운영 콘솔을 안내하는 스노스" priority/>
      <div>
        <p className="eyebrow">NEXPA Production &amp; Partner Console</p>
        <h1>기관을 받고, 섞지 않고, 되돌립니다</h1>
        <p>테넌트마다 데이터 모드가 붙습니다. 하나의 리포트는 하나의 모드만 담고, 실서비스 숫자와 체험 숫자가 같은 합계에 들어가지 않습니다.</p>
      </div>
    </section>

    <Card>
      <div className="card-heading"><div><span>테넌트</span><h2>계약 단위로 나뉩니다</h2></div><strong>{demoTenants.length}곳</strong></div>
      <div className="console-tenant-list-2-24">
        {demoTenants.map((tenant) => (
          <div key={tenant.id} className={tenant.dataMode === mode ? 'in-mode' : ''}>
            <b>{tenant.name}</b>
            <span className={`console-mode-badge-2-24 mode-${tenant.dataMode}`}>{dataModeLabels224[tenant.dataMode]}</span>
            <small>{tenantKindLabels224[tenant.kind]} · {tenantStatusLabels224[tenant.status]} · 좌석 {seatLabel224(tenant.children, tenant.seatLimit)}</small>
          </div>
        ))}
      </div>
      <p className="console-note-2-24">개인 가입 가족도 하나의 테넌트입니다. 소속 없음을 예외로 두면 조회마다 분기가 하나씩 늘고, 그중 하나를 빠뜨리면 격리가 뚫립니다.</p>
    </Card>

    <Card>
      <div className="card-heading"><div><span>운영 리포트</span><h2>모드를 섞지 않습니다</h2></div><strong>{inMode.length}곳</strong></div>
      <div className="console-mode-switch-2-24">
        {DATA_MODES224.map((item) => (
          <button type="button" key={item} className={item === mode ? 'selected' : ''} onClick={() => setMode(item)}>
            {dataModeLabels224[item]}
          </button>
        ))}
      </div>
      <p className="console-caption-2-24">{reportCaption224('nexpa', mode, `테넌트 ${inMode.length}곳`)}</p>
      <div className="console-report-grid-2-24">
        <Metric label="아이" value={`${totals.children.toLocaleString('ko-KR')}명`}/>
        <Metric label="30일 활동" value={`${totals.activeChildren.toLocaleString('ko-KR')}명`}/>
        <Metric label="학급" value={`${totals.classrooms}개`}/>
        <Metric label="계약 좌석" value={`${totals.seats.toLocaleString('ko-KR')}석`}/>
      </div>
      <p className="console-note-2-24">
        {mode === 'real'
          ? '영업 시연 한 번에 생기는 아이 30명이 월간 활성 사용자에 들어가면, 그 숫자로 내리는 모든 판단이 틀립니다.'
          : '체험 기관도 자기 리포트를 그대로 봅니다. 제외하는 것이 아니라 섞지 않는 것입니다.'}
      </p>
    </Card>

    <Card>
      <div className="card-heading"><div><span>온보딩</span><h2>5단계를 모두 기록해야 운영이 시작됩니다</h2></div><strong>{steps.length}/5</strong></div>
      <div className="console-step-list-2-24">
        {ONBOARDING_STEPS224.map((step) => (
          <button type="button" key={step} className={steps.includes(step) ? 'done' : ''} onClick={() => toggleStep(step)}>
            <b>{onboardingStepLabels224[step]}</b>
            <span>{steps.includes(step) ? '완료' : '남음'}</span>
          </button>
        ))}
      </div>
      <div className={`console-activation-2-24 ${activationRefusal ? 'refused' : 'ready'}`}>
        {activationRefusal ? activationRefusalLabel224(activationRefusal) : '운영 시작 가능 — 모든 단계가 기록되었습니다.'}
      </div>
      <p className="console-note-2-24">순서는 강제하지 않습니다. 현장에서는 학급을 먼저 만들고 계약서를 나중에 받는 일이 흔하고, 순서를 강제하면 담당자가 가짜 값으로 통과시킵니다.</p>
    </Card>

    <Card>
      <div className="card-heading"><div><span>통합 관리자</span><h2>연결 안 됨과 0은 다릅니다</h2></div><strong>2/5</strong></div>
      <div className="console-service-list-2-24">
        {demoServices.map((row) => (
          <div key={row.serviceId} className={row.status === 'pending' ? 'pending' : ''}>
            <b>{serviceLabels224[row.serviceId]}</b>
            <span>{serviceCountLabel224(row, row.events)}</span>
            <small>{row.status === 'pending' ? '어댑터 연결 대기' : `연결 사용자 ${serviceCountLabel224(row, row.linkedUsers)}명`}</small>
          </div>
        ))}
      </div>
      <p className="console-note-2-24">활동이 0건인 서비스와 아직 붙지 않은 서비스가 화면에서 똑같이 0으로 보이면, 운영자는 살아 있는 서비스가 죽었다고 판단합니다.</p>
    </Card>

    <Card>
      <div className="card-heading"><div><span>백업 · 복원</span><h2>복원은 기본이 거절입니다</h2></div><strong>{demoRefusals.length}가지</strong></div>
      <div className="console-refusal-list-2-24">
        {demoRefusals.map((reason) => (
          <button type="button" key={reason} className={reason === refusal ? 'selected' : ''} onClick={() => setRefusal(reason)}>{reason}</button>
        ))}
      </div>
      <div className="console-activation-2-24 refused">{refusal ? restoreRefusalLabel224(refusal) : '거절 사유를 눌러 보세요.'}</div>
      <p className="console-note-2-24">복원 응답에는 방금 덮어쓴 상태의 전체 백업이 들어 있습니다. 되돌리고 싶으면 받은 응답을 그대로 다시 복원하면 됩니다 — 복원 전에 백업 뜨는 것을 잊는 쪽이 정상이라고 가정합니다.</p>
    </Card>

    <div className="demo-213-actions">
      <SecondaryButton onClick={() => { setMode('real'); setSteps(['contract', 'admin', 'organization']); setRefusal(''); }}>초기화</SecondaryButton>
      <PrimaryButton onClick={() => setSteps([...ONBOARDING_STEPS224])}>온보딩 완료</PrimaryButton>
    </div>
  </AppFrame>;
}

export function AdminPartnerConsoleScreen224() {
  const { state } = useAppStore();
  const [overview, setOverview] = useState<ConsoleOverview224 | null>(null);
  const [tenants, setTenants] = useState<ConsoleTenant224[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [restrictions, setRestrictions] = useState<Record<string, DemoFeature224[]>>({});
  const [saved, setSaved] = useState('');

  // Only demo tenants can be restricted; applying a list to a real tenant would
  // silently disable a paying customer's features.
  const restrictableTenants = tenants.filter((tenant) => tenant.dataMode !== 'real');
  const toggle = (tenantId: string, feature: DemoFeature224) => setRestrictions((current) => {
    const list = current[tenantId] ?? [];
    return { ...current, [tenantId]: list.includes(feature) ? list.filter((item) => item !== feature) : [...list, feature] };
  });
  const save = async (tenantId: string) => {
    const ok = await saveRestrictedFeatures224(tenantId, restrictions[tenantId] ?? [], state?.session.csrfToken ?? '');
    setSaved(ok ? tenantId : '');
  };

  useEffect(() => {
    let active = true;
    void Promise.all([fetchConsoleOverview224(), fetchConsoleTenants224()]).then(([snapshot, list]) => {
      if (!active) return;
      setOverview(snapshot);
      const rows = list?.tenants ?? [];
      setTenants(rows);
      setRestrictions(Object.fromEntries(rows.map((tenant) => [tenant.id, (tenant.restrictedFeatures ?? []) as DemoFeature224[]])));
      setLoaded(true);
    });
    return () => { active = false; };
  }, []);

  if (!loaded) return <AppFrame title="파트너 콘솔" navigation="admin" screenClass="console-screen-2-24"><div className="loading-screen">운영 상태를 불러오고 있어요…</div></AppFrame>;

  return <AppFrame title="파트너 콘솔" navigation="admin" screenClass="console-screen-2-24">
    <StatusBanner />
    {!overview && <div className="console-activation-2-24 refused">운영 API에 연결할 수 없습니다. 숫자를 추측해 채우지 않습니다.</div>}

    {overview && <>
      <div className="console-report-grid-2-24">
        <Metric label="테넌트" value={`${overview.tenants.total}곳`} note={`운영 ${overview.tenants.active} · 온보딩 ${overview.tenants.onboarding}`}/>
        <Metric label="아이" value={`${overview.accounts.children}명`} note={`가족 ${overview.accounts.families}`}/>
        <Metric label="감사 로그" value={`${overview.audit.stored}건`} note={`한도 ${overview.audit.limit} · 누적 삭제 ${overview.audit.droppedCount}`}/>
        <Metric label="백업 기록" value={`${overview.backups.stored}건`} note={overview.backups.lastAt ? new Date(overview.backups.lastAt).toLocaleString('ko-KR') : '없음'}/>
      </div>

      <Card>
        <div className="card-heading"><div><span>서비스</span><h2>연결 상태</h2></div><strong>{overview.services.filter((row) => row.status === 'connected').length}/{overview.services.length}</strong></div>
        <div className="console-service-list-2-24">
          {overview.services.map((row) => (
            <div key={row.serviceId} className={row.status === 'pending' ? 'pending' : ''}>
              <b>{serviceLabels224[row.serviceId] ?? row.serviceId}</b>
              <span>{serviceCountLabel224(row, row.events)}</span>
              <small>{row.status === 'pending' ? '어댑터 연결 대기' : `연결 사용자 ${serviceCountLabel224(row, row.linkedUsers)}명`}</small>
            </div>
          ))}
        </div>
      </Card>
    </>}

    <Card>
      <div className="card-heading"><div><span>테넌트</span><h2>계약 단위</h2></div><strong>{tenants.length}곳</strong></div>
      <div className="console-tenant-list-2-24">
        {tenants.map((tenant) => (
          <div key={tenant.id}>
            <b>{tenant.name}</b>
            <span className={`console-mode-badge-2-24 mode-${tenant.dataMode}`}>{dataModeLabels224[tenant.dataMode] ?? tenant.dataMode}</span>
            <small>
              {tenantStatusLabels224[tenant.status as TenantStatus224] ?? tenant.status} · 학급 {tenant.classrooms}개 · 좌석 {seatLabel224(tenant.seats.used, tenant.seats.seatLimit)}
              {tenant.activationRefusal ? ` · ${activationRefusalLabel224(tenant.activationRefusal)}` : ''}
            </small>
          </div>
        ))}
      </div>
      {!tenants.length && <div className="empty-state">읽을 수 있는 테넌트가 없습니다.</div>}
    </Card>

    {restrictableTenants.map((tenant) => (
      <Card key={`restrict-${tenant.id}`}>
        <div className="card-heading"><div><span>체험 제한</span><h2>{tenant.name}</h2></div><strong>{(restrictions[tenant.id] ?? []).length}개 잠금</strong></div>
        <p className="console-note-2-24">체험 계정이 실제로 수행하면 곤란한 기능을 고릅니다. 화면에서 감추는 것이 아니라 <b>서버가 거절</b>합니다.</p>
        <div className="console-restrict-list-2-24">
          {DEMO_FEATURES224.map((feature) => {
            const on = (restrictions[tenant.id] ?? []).includes(feature);
            return <label key={feature} className={on ? 'on' : ''}>
              <input type="checkbox" checked={on} onChange={() => toggle(tenant.id, feature)}/>
              <span><b>{demoFeatureLabels224[feature]}</b><small>{demoFeatureNotes224[feature]}</small></span>
            </label>;
          })}
        </div>
        <div className="demo-213-actions">
          <SecondaryButton onClick={() => setRestrictions((current) => ({ ...current, [tenant.id]: [] }))}>모두 허용</SecondaryButton>
          <PrimaryButton onClick={() => void save(tenant.id)}>{saved === tenant.id ? '저장됨' : '제한 저장'}</PrimaryButton>
        </div>
      </Card>
    ))}
  </AppFrame>;
}
